import { useCallback } from 'react';
import { Text } from '@fluentui/react-components';
import { ArrowExportRegular, FilterRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { SearchBar } from '@/components/common/SearchBar';
import { useEventListToolbarStyles } from './EventListToolbar.styles';

export interface EventListToolbarProps {
  /** Server-side count of events matching the active filter/sort context. */
  totalCount: number;
  /** Events currently loaded into the grid (sum of all batches fetched so far). */
  loadedCount: number;
  /** Events loaded for the active server-side search query (across fetched batches). */
  searchMatchCount: number;
  /** Whether a non-empty search query is currently active. */
  hasSearchQuery: boolean;
  hasActiveFilters: boolean;
  onFilterClick: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onExport: () => void;
  isExporting?: boolean;
}

export const EventListToolbar = ({
  totalCount,
  loadedCount,
  searchMatchCount,
  hasSearchQuery,
  hasActiveFilters,
  onFilterClick,
  searchQuery,
  onSearchChange,
  onExport,
  isExporting,
}: EventListToolbarProps) => {
  const styles = useEventListToolbarStyles();
  const { t } = useTranslation('planner');

  const handleSearchChange = useCallback(
    (value: string) => {
      onSearchChange(value);
    },
    [onSearchChange]
  );

  const shownCount = hasSearchQuery ? searchMatchCount : loadedCount;
  const resultLabel = t('filter.showingResults', {
    filtered: shownCount,
    total: totalCount,
  });

  return (
    <div className={styles.root} data-testid="event-list-toolbar">
      <div className={styles.searchContainer}>
        <SearchBar
          placeholder={t('eventList.searchPlaceholder')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <BasicButton
        className={styles.filterButton}
        variant="secondary"
        size="medium"
        icon={<FilterRegular />}
        onClick={onFilterClick}
        data-testid="filter-button"
      >
        {t('filter.buttonLabel')}
      </BasicButton>
      <Text className={styles.resultCount} data-testid="results-count">
        {resultLabel}
      </Text>
      <div className={styles.spacer} />
      <BasicButton
        variant="primary"
        size="medium"
        icon={<ArrowExportRegular />}
        onClick={onExport}
        disabled={isExporting}
        aria-busy={isExporting}
        data-testid="export-data-button"
      >
        {hasActiveFilters ? t('eventList.exportFilteredData') : t('eventList.exportAllData')}
      </BasicButton>
    </div>
  );
};

EventListToolbar.displayName = 'EventListToolbar';
